import { Component, OnInit, Input, Output, Inject, EventEmitter } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { User } from 'src/app/services/data/users/user.class';
import { Space } from 'src/app/services/data/spaces/space.class';
import { BaseComponent } from '../../shared/base.component';
import { DialogEvent } from 'src/app/shared/utilities/types.utilities';
import { map, startWith } from 'rxjs/operators';

import * as dayjs from 'dayjs';

export interface InputsModalData {
    user: User;
    room: Space;
}

@Component({
    selector: 'app-inputs-modal',
    templateUrl: './inputs-modal.component.html',
    styleUrls: ['./inputs-modal.component.scss'],
})
export class InputsModalComponent extends BaseComponent implements OnInit {
    /** Emitter for user action on the modal */
    @Output() public event = new EventEmitter<DialogEvent>();
    /** User data */
    @Input() user: User;
    /** Room data */
    @Input() room: Space;
    /** Clinicians options */
    public clinicians: User[] = [];
    /** Clinicians options */
    public filteredClinicians: User[] = [];
    /** Clinician */
    public clinician: string;
    /** Time of appointment in ms epoch */
    public appointment_time: number;
    /** Form group */
    public form: FormGroup;
    /** patient name */
    public patient_name: string;
    /** Time string */
    public time_display: string;
    /** Show time picker */
    public show_picker: boolean;

    constructor(
        private dialogRef: MatDialogRef<InputsModalComponent>,
        @Inject(MAT_DIALOG_DATA) private _data: InputsModalData
    ) {
        super();
    }

    public ngOnInit(): void {
        this.user = this._data?.user;
        this.room = this._data?.room;
        this.initTime();
        this.initForm();
    }

    public initTime(): void {
        const now = dayjs();
        const roundedMinutes = Math.ceil(now.minute() / 5) * 5;
        this.appointment_time = dayjs().minute(roundedMinutes).startOf('m').valueOf();
    }

    public initForm(): void {
        const group: any = {
            appointment_time: new FormControl(this.appointment_time),
        };
        if (this.user.type !== 'patient') {
            group.patient_name = new FormControl('', Validators.required);
            group.with_patient = new FormControl('', Validators.required);
        }
        // Add clinicians dropdown list
        if (
            this.user.type !== 'employee' &&
            this.room.list_member_names &&
            this.room.list_member_names.length > 0
        ) {
            const sorted_list = this.room.list_member_names.sort((a, b) => {
                return (a.name || '').localeCompare(b.name || '');
            });
            this.clinicians = [{ id: 0, name: `I don't know` }, ...sorted_list];
            group.clinician = new FormControl('', Validators.required);
            this.filteredClinicians = group.clinician.valueChanges.pipe(
                startWith(''),
                map((user: User) => (typeof user === 'string' ? user : user.name)),
                map((name: string) => this._filterClinicians(name))
            );
        }
        if (this.user.type === 'employee' || this.user.type === 'another provider') {
            group.job_title = new FormControl('', Validators.required);
        }
        this.form = new FormGroup(group);
    }

    public toggleTimePicker() {
        this.show_picker = !this.show_picker;
    }

    public closeTimePicker(): void {
        this.show_picker = false;
    }

    public changeTime(e: number): void {
        this.appointment_time = e;
        this.form.patchValue({ appointment_time: e });
        this.closeTimePicker();
    }

    public close() {
        this.dialogRef.close();
    }

    public accept() {
        if (!this.form.value.clinician || !this.form.value.clinician.id) {
            this.form.patchValue({
                clinician: {},
            });
        }
        this.event.emit({ reason: 'done', metadata: this.form.value });
        this.close();
    }

    public clinicianName(clinician: User): string {
        return clinician?.name || '';
    }

    private _filterClinicians(name: string): User[] {
        const regex = new RegExp(name, 'i');
        const filtered = this.clinicians.filter((user) => regex.test(user.name));
        return filtered.length > 0 ? filtered : this.clinicians;
    }
}
